import {
  Pill,
  Tile,
  TileContent,
  TileMedia,
  TileText,
  TileTitle,
} from '@design-system';
import { isSoldOut } from '@utils/product';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';

import { useProducts } from '../../hooks/useProducts';
import { ProductIdStoryblok } from '../../types/generated-storyblok';
import { renderShopifyArtistName } from '../../utils/artists';
import { productUrl } from '../../utils/productUrl';
import { shopifyAdminIdToGraphqlId } from '../../utils/shopifyIds';
import ProductDate from '../Containers/ProductDate';
import { Image } from '../Image';
import {
  getProductTilePill,
  getProductTileStatus,
} from '../ProductTile/utils/status';
import { ArticleItemSkeleton } from './ArticleItemSkeleton';

export const ContentProduct = ({ blok }: { blok: ProductIdStoryblok }) => {
  const { t } = useTranslation('product');
  const { loading, products } = useProducts([
    shopifyAdminIdToGraphqlId(blok.id!),
  ]);
  const [product] = products;

  if (loading || !product) {
    return <ArticleItemSkeleton variant="list" />;
  }

  const tilePill = getProductTilePill(product);
  const tileStatus = getProductTileStatus(product);

  return (
    <Tile key={product.id} className="group py-4" direction="row">
      <TileMedia className="size-16 shrink-0">
        <Image
          alt={product.alt || product.title}
          src={product.image}
          hoverEffect
          width={64}
        />
      </TileMedia>
      <TileContent className="w-full" direction="column">
        <TileTitle>
          <Link
            href={productUrl(product.full_slug || '', product.productType)}
            prefetch={false}
            className="after:absolute after:inset-0 after:block after:content-['']"
          >
            {renderShopifyArtistName(product.artist)}
          </Link>
        </TileTitle>

        <TileText>{product.title}</TileText>

        {tileStatus ? (
          <TileText variant={tileStatus.isActive ? 'active' : 'muted'}>
            {tileStatus.variant === 'text' ? (
              t(tileStatus.key)
            ) : (
              <ProductDate
                date={tileStatus.date}
                keyCountdownDate={`${tileStatus.key}.date`}
                keyCountdownDays={`${tileStatus.key}.days`}
                keyCountdownTime={`${tileStatus.key}.time`}
                uniqueDateFormat="dd MMMM"
              />
            )}
          </TileText>
        ) : null}
      </TileContent>

      <TileContent className="shrink-0" direction="row">
        {!isSoldOut(product) && tilePill ? (
          <Pill isActive={tilePill.isActive} label={t(tilePill.key)} />
        ) : null}
      </TileContent>
    </Tile>
  );
};
